import {Plot} from './plot';
import {onFrame} from '../../cdlx/utils/wait';
import {StableLabeler as Labeler} from './stable-labeler';

/**
 * @property {number} notHoveredOpacity=0.2 Opacity applied to the other traces when a specific one is hovered
 * @property {number} notHoveredOpacityOfScatters=0.1 Opacity applied to the other traces when a specific one is hovered
 * @property {number} fullOpacity=1 Opacity 100% to show elements
 * @property {number} zeroOpacity=0 Zero opacity to hide elements
 *
 * @memberof ScatterPlot
 * @extends Plot.conf
 */
const conf = {
	...Plot.conf,
	notHoveredOpacity: 0.2,
	notHoveredOpacityOfScatters: 0.1,
	fullOpacity: 1,
	zeroOpacity: 0,
	unhoverTimeoutDuration: 30,
	subplotBoxSelector: '.cartesianlayer .subplot.xy .gridlayer',
	annotationSelector: '.annotation[data-index="{index}"]',
	annotationTextSelector: '.annotation-text-g',
	annotationArrowSelector: '.annotation-arrow-g',
	shapeSelector: '.layer-above .shapelayer .shape-group',
	labelPadding: 0,
	anchorRadius: 6,
	ndcShapesName: 'Country targets',
};

/**
 * classdesc Component used to wrap Plotly scatter plots (emissions plots + eventual levels representation)
 *
 * @class ScatterPlot
 * @extends {Plot}
 */
class ScatterPlot extends Plot {
	static conf = conf;

	// static defaultOpts = {
	// 	...Plot.defaultOpts,
	// };

	hoveredTraceIndex = null;
	unhoverTimeout = null;
	hoveredDataCopy = null;
	columnsAmount = 0;
	rowsAmount = 1;
	layoutChanges = {};

	async initPlot() {
		await super.initPlot();
		// register the needed events listeners
		this.plot.on('plotly_hover', this.onHover.bind(this));
		this.plot.on('plotly_unhover', this.onUnhover.bind(this));
		this.plot.on('plotly_legendclick', this.onLegendClick.bind(this));
		this.plot.on('plotly_legenddoubleclick', this.onLegendDoubleClick.bind(this));
	}

	/**
	 * Handle the hover event
	 * @see {@link https://plotly.com/javascript/plotlyjs-events/}
	 *
	 * @param {object} data values referred to the hovered trace
	 * @memberof ScatterPlot
	 */
	onHover(data) {
		if (this.unhoverTimeout) {
			clearTimeout(this.unhoverTimeout);
		}
		const traceIndex = data.points[0].curveNumber;
		// avoid to do the same work multiple times
		if (traceIndex === this.hoveredTraceIndex) {
			return;
		}
		if (!this.hoveredDataCopy) {
			this.hoveredDataCopy = Plot.deepClone(this.plot.data);
		}
		this.hoveredTraceIndex = traceIndex;
		const hoveredTraceGroup = [];
		const traceOpacities = [];
		const legendGroup = this.plot.data[traceIndex].legendgroup;
		const xaxis = this.plot.data[traceIndex].xaxis;
		const yaxis = this.plot.data[traceIndex].yaxis;
		for (let i = 0; i < this.plot.data.length; i++) {
			traceOpacities[i] =
				i === traceIndex ||
				(legendGroup &&
					legendGroup.length &&
					this.plot.data[i].legendgroup === legendGroup &&
					(xaxis !== this.plot.data[i].xaxis || yaxis !== this.plot.data[i].yaxis))
					? 1
					: conf.notHoveredOpacity;
			if (traceOpacities[i] === 1) {
				hoveredTraceGroup.push(i);
			}
		}

		// when a trace is hovered, we reduce the opacity of all the other traces
		// const traceOpacities = Array.from({length: this.plot.data.length}).fill(conf.notHoveredOpacity);
		// traceOpacities[traceIndex] = 1;

		// when the trace is hovered we change alpha value of it's text for markers
		const textColors = this.hoveredDataCopy.map((trace, i) => {
			if (hoveredTraceGroup.includes(i) && trace.textfont && trace.textfont.color) {
				return trace.textfont.color.replace(/[^,]+(?=\))/, conf.fullOpacity);
			}
			return 'transparent';
		});

		// when a trace is hovered we also lower opacity of scatter traces by adjusting RGBA value of it's fillcolor
		const fillColors = this.hoveredDataCopy.map((trace, i) => {
			if (trace.type === 'scatter' && trace.fillcolor && trace.hoveron) {
				return hoveredTraceGroup.includes(i)
					? trace.fillcolor
					: trace.fillcolor.replace(/[^,]+(?=\))/, conf.notHoveredOpacityOfScatters);
			}
			return trace.fillcolor;
		});

		// when the trace is hovered we set marker's opacity to visible
		const markerOpacities = this.hoveredDataCopy.map((trace, i) => {
			if (hoveredTraceGroup.includes(i) && Array.isArray(trace.marker.opacity)) {
				return trace.marker.opacity.map((opacity) => (opacity === 0 ? 1 : opacity));
			}
			return [...trace.marker.opacity];
		});

		this.plotly.restyle(this.plot, {
			opacity: traceOpacities,
			fillcolor: fillColors,
			'textfont.color': textColors,
			'marker.opacity': markerOpacities,
		});
	}

	/**
	 * Handle the unhover event by delayed execution
	 *
	 * @see {@link https://plotly.com/javascript/plotlyjs-events/}
	 *
	 * @param {object} data values referred to the unhovered trace
	 * @memberof ScatterPlot
	 */
	onUnhover(data) {
		this.unhoverTimeout = setTimeout(() => this.unhover(data), conf.unhoverTimeoutDuration);
	}

	/**
	 * Unhover execution
	 *
	 * @param {object} data values referred to the unhovered trace
	 * @memberof ScatterPlot
	 */
	unhover(data) {
		this.hoveredTraceIndex = null;
		// restoring all scatter values changed in the hover
		const changes = {
			opacity: conf.fullOpacity,
			'textfont.color': this.hoveredDataCopy.map((trace) => {
				if ('textfont' in trace && 'color' in trace.textfont) {
					return trace.textfont.color;
				}
				return null;
			}),
			fillcolor: this.hoveredDataCopy.map((trace) => trace.fillcolor),
			'marker.opacity': this.hoveredDataCopy.map((trace) => [...trace.marker.opacity]),
		};
		this.hoveredDataCopy = null;
		this.plotly.restyle(this.plot, changes);
	}

	async onLegendClick(data) {
		this.toggleNdcsVisibility(data);
	}

	onLegendDoubleClick(data) {
		return false;
		// this.toggleNdcsVisibility();
	}

	async toggleNdcsVisibility(data) {
		const shapes = this.plot.layout.shapes;
		if (!shapes || !shapes.length) {
			return;
		}
		const ndcIndexes = [];
		const shapeNames = {};
		let ndcLegendShapeVisible = null;
		const hasTargetYear = 'targetYear' in this.controls;
		const targetYearTag = 'targetYear';
		const targetYear = hasTargetYear ? String(this.controls.targetYear) : null;
		for (let i = 0; i < shapes.length; i++) {
			const shape = shapes[i];
			if (shape.name === conf.ndcShapesName && data.curveNumber === i) {
				// console.log('visible', shape.visible); //data.curveNumber
				ndcLegendShapeVisible = !(shape.visible === 'legendonly' ? false : !!shape.visible);
			} else if (shape.name && shape.name.length) {
				const names = shape.name.split(',');
				if (names.includes('ndc')) {
					if (!hasTargetYear || (names.includes(targetYearTag) && names.includes(targetYear))) {
						ndcIndexes.push(i);
						shapeNames[shape.name] = true;
					}
				}
			}
		}
		if (ndcLegendShapeVisible !== null && ndcIndexes.length) {
			const updates = {};
			for (const ndcIndex of ndcIndexes) {
				updates['shapes[' + ndcIndex + '].visible'] = ndcLegendShapeVisible;
			}
			for (let i = 0; i < this.plot.layout.annotations.length; i++) {
				const annotation = this.plot.layout.annotations[i];
				if (annotation.name && annotation.name in shapeNames) {
					// const names = annotation.name.split(',');
					// if (names.includes('ndc')) {
					// 	if (
					// 		ndcLegendShapeVisible === false ||
					// 		!hasTargetYear ||
					// 		(names.includes(targetYearTag) && names.includes(targetYear))
					// 	) {
					updates['annotations[' + i + '].visible'] = ndcLegendShapeVisible;
					// }
				}
			}
			await this.plotly.relayout(this.plot, updates);
		}
	}

	preparePlotParams() {
		const {data, layout} = super.preparePlotParams();

		const options = this.opts.options;
		// increasing the revision numbers ensures that the new data will be completely processed

		if ('referenceYear' in this.controls && 'unit' in this.controls) {
			// referenceYear should be a number
			const referenceYear = +this.controls.referenceYear;
			const unit = this.controls.unit;
			const referenceValue = Plot.extractValue(data, referenceYear);

			// reposition the referenceYear point and line
			for (const shape of layout.shapes) {
				if (shape.customdata && shape.customdata.includes('referenceYearPoint')) {
					shape.xanchor = referenceYear;
					shape.yanchor = referenceValue;
				}
				if (shape.customdata && shape.customdata.includes('referenceYearLine')) {
					shape.y0 = referenceValue;
					shape.y1 = referenceValue;
				}
				// if (shape.customdata && shape.customdata.includes('referenceYearLine')) { // trying to control the reference line
				// 	shape.layer = 'below';
				// 	shape.line.color = 'grey';
				// 	shape.line.opacity = 1;
				// 	//console.log(layout.shapes);
				// }
			}
			// reposition the referenceYear annotation
			for (const annotation of layout.annotations) {
				if (annotation.name === 'referenceYearAnnotation') {
					annotation.x = referenceYear;
					annotation.y = referenceValue;
					if ('referenceYearAnnotation' in options.annotations) {
						const opts = options.annotations.referenceYearAnnotation;
						const textTpl = opts.textTpl;
						annotation.text = this.formatText(textTpl, {
							year: referenceYear,
							value:
								unit === 'percent'
									? ''
									: this.numberFormat(
											referenceValue,
											options.decimals,
											options.decimalSeparator,
											options.thousandsSeparator
									  ),
							unit: unit === 'percent' ? '' : opts.unit,
						});
					}
				}
			}

			// when the unit is percent, we recalculate all the Y values as a percent of the Y value in the referenceYear
			if (unit === 'percent') {
				layout.yaxis.ticksuffix = '%';
				if ('yaxis2' in layout) {
					layout.yaxis2.ticksuffix = '%';
				}

				for (const trace of data) {
					trace.meta[0] = '%';
					trace.meta[1] = '%';
					for (let i = 0; i < trace.y.length; i++) {
						const originalValue = trace.y[i];
						const newValue = this.absToPercent(referenceValue, originalValue);
						trace.y[i] = newValue;
					}
				}
				// we do the same for shapes, as long as their Y coordinate space is not domain-related or 'paper'.
				for (const shape of layout.shapes) {
					const ySizeMode = 'ysizemode' in shape ? shape.ysizemode : 'scaled';
					const yref = 'yref' in shape ? shape.yref : '';
					if (yref !== 'paper' && !yref.includes('domain')) {
						// if yszizemode is scaled we transform the coordinate, otherwise the anchor
						if (ySizeMode === 'scaled') {
							shape.y0 = this.absToPercent(referenceValue, shape.y0);
							shape.y1 = this.absToPercent(referenceValue, shape.y1);
						} else {
							shape.yanchor = this.absToPercent(referenceValue, shape.yanchor);
						}
					}
				}

				// we do the same for annotations, as long as their Y coordinate space is not domain-related or 'paper'.
				for (const annotation of layout.annotations) {
					const yref = 'yref' in annotation ? annotation.yref : '';
					if (yref !== 'paper' && !yref.includes('domain')) {
						annotation.y = this.absToPercent(referenceValue, annotation.y);
					}
					const name = annotation.name;
					if (name in options.annotations) {
						const opts = options.annotations[name];
						if (opts.percent && 'value' in opts && 'textTpl' in opts) {
							const originalValue = opts.value;
							let newValue = this.absToPercent(referenceValue, originalValue, !!opts.gapPercent);
							newValue =
								(Math.round(newValue) > 0 ? '+' : '') +
								this.numberFormat(
									newValue,
									options.decimals,
									options.decimalSeparator,
									options.thousandsSeparator
								);
							const textTpl = opts.hidePercent
								? opts.textTpl.replace('{{value}}', '').replace('{{unit}}', '')
								: opts.textTpl;
							annotation.text = this.formatText(textTpl, {value: newValue, unit: '%'});
						}
					}
				}
			}
		}

		if ('targetYear' in this.controls) {
			const targetYear = String(this.controls.targetYear);
			const targetYearTag = 'targetYear';

			for (const shape of layout.shapes) {
				if ('name' in shape && shape.name) {
					const names = shape.name.split(',');
					if (names.includes(targetYearTag)) {
						// console.log('shape', shape.xref, names);
						// if (names.includes('ndc')) {
						// 	shape.visible = true;
						// 	shape.opacity = names.includes(targetYear) ? 1 : 0.5;
						// } else {
						shape.visible = names.includes(targetYear);
						// }
					}
				}
			}

			for (const annotation of layout.annotations) {
				if ('name' in annotation && annotation.name) {
					const names = annotation.name.split(',');
					if (names.includes(targetYearTag)) {
						annotation.visible = names.includes(targetYear);
					}
				}
			}
		}

		return {
			data: data,
			layout: layout,
		};
	}

	/**
	 * Convert an absolute value to a percent relative to a reference value
	 *
	 * @param {number} referenceValue the reference value
	 * @param {number} originalValue the original value to be converted in percent
	 * @returns {number} a percent value, already in [0, 100] range for readability
	 * @memberof ScatterPlot
	 */
	absToPercent(referenceValue, originalValue, gapPercent = false) {
		if (referenceValue === 0) {
			return 0;
		}

		return ((originalValue - (gapPercent ? 0 : referenceValue)) / referenceValue) * 100;
	}

	async refreshPlot() {
		const {data, layout} = this.preparePlotParams();
		this.updateGridAndSizing(layout);
		await onFrame(async () => {
			await this.plotly.react(this.plot, data, layout, this.opts.config);
			// needed because on data changes it randomly generates a plot with height = 0 :|
			if (this.relayoutRequired) {
				this.relayoutRequired = false;
				await this.forceRelayout();
			}
			await this.processAnnotationsOverlapping(layout);
		});
	}

	async onResize(event) {
		const {data, layout} = this.preparePlotParams();
		this.updateGridAndSizing(layout);
		await onFrame(async () => {
			await this.plotly.react(this.plot, data, layout, this.opts.config);
			// needed because on data changes it randomly generates a plot with height = 0 :|
			if (this.relayoutRequired) {
				this.relayoutRequired = false;
				await this.forceRelayout();
			}
			await this.processAnnotationsOverlapping(layout);
		});

		// await this.updateGridAndSizing();
		// await waitMs(30);
		// await this.processAnnotationsOverlapping(this.plot.layout);
	}

	async processAnnotationsOverlapping(layout) {
		if (!layout.annotations || !layout.annotations.length) {
			return;
		}
		const labels = [];
		const shapes = [];
		const padding = conf.labelPadding;
		const origin = this.imageMode ? this.plotImage : this.plot;
		const boxElement = origin.querySelector(conf.subplotBoxSelector);
		if (!boxElement) {
			return;
		}
		const boxRect = boxElement.getBoundingClientRect();
		// console.log('boxElement', boxElement);
		for (let i = 0; i < layout.annotations.length; i++) {
			const annotation = layout.annotations[i];
			if (
				(annotation.visible || !('visible' in annotation)) &&
				(!('xref' in annotation) || annotation.xref === 'x')
			) {
				const element = origin.querySelector(conf.annotationSelector.replace('{index}', i));
				if (element) {
					const text = element.querySelector(conf.annotationTextSelector);
					const arrow = element.querySelector(conf.annotationArrowSelector);
					if (!text || !arrow) {
						continue;
					}
					const textRect = text.getBoundingClientRect();
					const arrowRect = arrow.getBoundingClientRect();
					const label = {
						// x1: textRect.left - boxRect.left - padding,
						// x2: textRect.left - boxRect.left + textRect.width + padding,
						// y1: textRect.top - boxRect.top - padding,
						// y2: textRect.top - boxRect.top + textRect.height + padding,
						x: textRect.left - boxRect.left + textRect.width / 2,
						y: textRect.top - boxRect.top + textRect.height / 2,
						index: labels.length,
						width: textRect.width + padding,
						height: textRect.height + padding,
						name: i,
						cx: 0,
						cy: 0,
						cx1: 0,
						cx2: 0,
						cy1: 0,
						cy2: 0,
						cr: conf.anchorRadius,
					};
					const ax = 'ax' in annotation ? annotation.ax : 0;
					const ay = 'ay' in annotation ? annotation.ay : 0;
					// const standoff = 'standoff' in annotation ? annotation.standoff : 0;

					let cx = null;
					let cy = null;
					let distance = null;
					const arrowRectCorrected = {
						left: arrowRect.left - boxRect.left,
						top: arrowRect.top - boxRect.top,
					};
					arrowRectCorrected.right = arrowRectCorrected.left + arrowRect.width;
					arrowRectCorrected.bottom = arrowRectCorrected.top + arrowRectCorrected.height;
					const points = [
						{x: arrowRectCorrected.left, y: arrowRectCorrected.top},
						{x: arrowRectCorrected.left, y: arrowRectCorrected.bottom},
						{x: arrowRectCorrected.right, y: arrowRectCorrected.top},
						{x: arrowRectCorrected.right, y: arrowRectCorrected.bottom},
					];
					const labelPos = {x: label.x, y: label.y};
					for (let k = 0; k < points.length; k++) {
						const point = points[k];
						const newDistance = this.distance(point, labelPos);
						if (distance === null || newDistance > distance) {
							cx = point.x;
							cy = point.y;
							distance = newDistance;
						}
					}

					// if (ax === 0) {
					// 	label.cx = label.x;
					// 	label.cy = label.y - ay - standoff;
					// } else if (ay === 0) {
					// 	label.cy = label.y;
					// 	label.cx = label.x - ax - standoff * Math.sign(ax);
					// } else {
					// 	const c1 = Math.abs(ax);
					// 	const c2 = Math.abs(ay);
					// 	const ip = Math.sqrt(c1 * c1 + c2 * c2);
					// 	const angle = Math.asin(c2 / ip);
					// 	const cs2 = standoff * Math.sin(angle);
					// 	const cs1 = (c1 / c2) * cs2;
					// 	const offsetX = ax + Math.sign(ax) * cs1;
					// 	const offsetY = ay + Math.sign(ay) * cs2;
					// 	label.cx = label.x - offsetX;
					// 	label.cy = label.y - offsetY;
					// }
					label.cx = cx;
					label.cy = cy;
					label.cx1 = label.cx - label.cr;
					label.cx2 = label.cx + label.cr;
					label.cy1 = label.cy - label.cr;
					label.cy2 = label.cy + label.cr;
					label.xSign = Math.sign(label.x - label.cx);
					label.ySign = Math.sign(label.y - label.cy);
					label.x = label.cx + ax;
					label.y = label.cy + ay;
					label.x1 = label.x - label.width / 2;
					label.x2 = label.x1 + label.width;
					label.y1 = label.y - label.height / 2;
					label.y2 = label.y1 + label.height;

					// console.log('label', i, annotation.text, ax, ay, label, textRect);
					labels.push(label);
				}
			}
		}

		if (!labels.length) {
			return;
		}

		const shapeElements = origin.querySelectorAll(conf.shapeSelector);
		for (const shapeElement of shapeElements) {
			const shapeRect = shapeElement.getBoundingClientRect();
			if (shapeRect.width > 0 && shapeRect.height > 0) {
				shapes.push({
					x1: shapeRect.left - boxRect.left,
					x2: shapeRect.left - boxRect.left + shapeRect.width,
					y1: shapeRect.top - boxRect.top,
					y2: shapeRect.top - boxRect.top + shapeRect.height,
					width: shapeRect.width,
					height: shapeRect.height,
				});
			}
		}

		const originalLabels = Plot.deepClone(labels);
		const labeler = new Labeler();
		const newLabels = labeler.run(labels, shapes, boxRect.width, boxRect.height);

		const updates = {};
		const annotations = layout.annotations;
		for (let i = 0; i < newLabels.length; i++) {
			const oldLabel = originalLabels[i];
			const newLabel = newLabels[i];
			const index = +newLabel.name;
			const diffX = newLabel.x - oldLabel.x;
			const diffY = newLabel.y - oldLabel.y;
			// console.log('diffs', i, diffX, diffY);
			const annotation = annotations[index];
			const ax = 'ax' in annotation ? annotation.ax : 0;
			const ay = 'ay' in annotation ? annotation.ay : 0;
			const newAx = ax + diffX;
			const newAy = ay + diffY;
			updates['annotations[' + index + '].ax'] = newAx;
			updates['annotations[' + index + '].ay'] = newAy;
			if (this.imageMode) {
				layout.annotations[index].ax = newAx;
				layout.annotations[index].ay = newAy;
			}
		}
		if (Object.keys(updates).length) {
			// console.log('updates', updates, origin, this.imageMode);
			await this.plotly.relayout(origin, updates);
		}
	}

	distance(a, b) {
		const dx = a.x - b.x;
		const dy = a.y - b.y;
		return Math.sqrt(dx * dx + dy * dy);
	}

	hasCollision(a, b) {
		return !(a.y2 < b.y1 || a.y1 > b.y2 || a.x2 < b.x1 || a.x1 > b.x2);
	}
}

export {ScatterPlot};
